'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var plugin = require('tailwindcss/plugin');

function _interopDefaultCompat (e) { return e && typeof e === 'object' && 'default' in e ? e : { default: e }; }

var plugin__default = /*#__PURE__*/_interopDefaultCompat(plugin);

/**
 * Typography token definitions - the source of truth for all typography styles.
 */ const typographyTokenDefinitions = {
    header: {
        '2xl': {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.5xl',
            lineHeight: 'lineHeight.12'
        },
        xl: {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.4xl',
            lineHeight: 'lineHeight.9'
        },
        lg: {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.3xl',
            lineHeight: 'lineHeight.7.5'
        },
        'lg-wrap': {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.3xl',
            lineHeight: 'lineHeight.9'
        },
        md: {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.2xl',
            lineHeight: 'lineHeight.7'
        },
        sm: {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.xl',
            lineHeight: 'lineHeight.6'
        },
        xs: {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.bold',
            fontSize: 'fontSize.lg',
            lineHeight: 'lineHeight.6'
        },
        '2xs': {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.bold',
            fontSize: 'fontSize.base',
            lineHeight: 'lineHeight.6'
        },
        '3xs': {
            color: 'textColor.neutral.strong',
            fontWeight: 'fontWeight.circular.bold',
            fontSize: 'fontSize.sm',
            lineHeight: 'lineHeight.5'
        },
        '4xs': {
            fontWeight: 'fontWeight.circular.bold',
            fontSize: 'fontSize.xs',
            lineHeight: 'lineHeight.4',
            textTransform: 'uppercase',
            letterSpacing: 'letterSpacing.wider'
        }
    },
    body: {
        '2xl': {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.2xl',
            lineHeight: 'lineHeight.8'
        },
        xl: {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.xl',
            lineHeight: 'lineHeight.7'
        },
        lg: {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.lg',
            lineHeight: 'lineHeight.7'
        },
        base: {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.base',
            lineHeight: 'lineHeight.6'
        },
        sm: {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.sm',
            lineHeight: 'lineHeight.5'
        },
        xs: {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.xs',
            lineHeight: 'lineHeight.4'
        },
        'base-bold': {
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.base',
            lineHeight: 'lineHeight.6'
        },
        'base-underline': {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.base',
            lineHeight: 'lineHeight.6',
            textDecoration: 'underline'
        },
        'sm-bold': {
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.sm',
            lineHeight: 'lineHeight.5'
        },
        'sm-underline': {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.sm',
            lineHeight: 'lineHeight.5',
            textDecoration: 'underline'
        },
        'xs-bold': {
            fontWeight: 'fontWeight.circular.medium',
            fontSize: 'fontSize.xs',
            lineHeight: 'lineHeight.4'
        },
        'xs-underline': {
            fontWeight: 'fontWeight.circular.regular',
            fontSize: 'fontSize.xs',
            lineHeight: 'lineHeight.4',
            textDecoration: 'underline'
        }
    }
};
/**
 * Converts typography token definitions to theme-resolved styles.
 * @param theme - Tailwind theme function for resolving theme values.
 * @returns Resolved typography styles object with actual CSS values.
 */ const getTypographyStyles = (theme)=>{
    const resolveThemeValue = (themeKey)=>{
        // Handle decimal theme keys using bracket notation (e.g., lineHeight.7.5, fontSize.1.5, etc.)
        // Look for pattern like "baseKey.number.number" where the last two parts form a decimal
        const parts = themeKey.split('.');
        if (parts.length >= 3) {
            // Check if last two parts form a decimal number (e.g., "7" + "5" = "7.5")
            const secondToLast = parts[parts.length - 2];
            const lastPart = parts[parts.length - 1];
            if (/^\d+$/.test(secondToLast) && /^\d+$/.test(lastPart)) {
                // Reconstruct: baseKey = everything except last two parts, subKey = decimal formed by last two parts
                const baseKey = parts.slice(0, -2).join('.');
                const subKey = `${secondToLast}.${lastPart}`;
                const baseValue = theme(baseKey);
                if (typeof baseValue === 'object' && subKey in baseValue) {
                    const resolvedValue = baseValue[subKey];
                    return String(resolvedValue);
                }
            }
        }
        const result = theme(themeKey);
        return typeof result === 'string' || typeof result === 'number' ? String(result) : 'N/A';
    };
    return Object.entries(typographyTokenDefinitions).reduce((acc, [category, variants])=>{
        const resolvedVariants = Object.entries(variants).reduce((variantAcc, [variant, tokenStyles])=>{
            const resolvedStyles = Object.entries(tokenStyles).reduce((styleAcc, [property, themeKey])=>{
                if (typeof themeKey === 'string' && property !== 'textTransform' && property !== 'textDecoration') {
                    return {
                        ...styleAcc,
                        [property]: resolveThemeValue(themeKey)
                    };
                }
                return {
                    ...styleAcc,
                    [property]: themeKey
                };
            }, {});
            return {
                ...variantAcc,
                [variant]: resolvedStyles
            };
        }, {});
        return {
            ...acc,
            [category]: resolvedVariants
        };
    }, {});
};
var typography = plugin__default.default(({ addUtilities, theme })=>{
    // NOTE: Euclid Typography components require Circular font to be enabled.
    if (!theme('fontFamily.circular')) {
        return;
    }
    const typographyTypes = getTypographyStyles(theme);
    /**
     * Nested reduce: loops through object and creates `type` utility classes.
     * @example
     * ```
     * {
     *    .type-header-2xl: {
     *      fontFamily: "Circular, sans-serif";
     *      color: "#3D3652";
     *      fontWeight: 500;
     *      fontSize: "3rem";
     *      lineHeight: "3rem";
     *    },
     *    .type-header-lg-wrap: {
     *      fontFamily: "Circular, sans-serif";
     *      color: "#3D3652";
     *      fontWeight: 500;
     *      fontSize: "1.875rem";
     *      lineHeight: "2.25rem";
     *    },
     *    ...
     *    .type-body-base: {
     *      fontFamily: "Circular, sans-serif";
     *      fontWeight: 400;
     *      fontSize: "1rem";
     *      lineHeight: "1.5rem";
     *    },
     *    .type-body-sm-bold: {
     *      fontFamily: "Circular, sans-serif";
     *      fontWeight: 700;
     *      fontSize: "0.875rem";
     *      lineHeight: "1.25rem";
     *    },
     *    ...
     * }
     * ```
     */ const typographyUtilityClasses = Object.entries(typographyTypes).reduce((typesAcc, [typeName, variants])=>{
        const classes = Object.entries(variants).reduce((variantAcc, [variantName, styles])=>{
            return {
                ...variantAcc,
                [`.type-${typeName}-${variantName}`]: {
                    fontFamily: theme(`fontFamily.circular`),
                    ...styles
                }
            };
        }, {});
        return {
            ...typesAcc,
            ...classes
        };
    }, {});
    addUtilities(typographyUtilityClasses);
});

exports.default = typography;
exports.typographyTokenDefinitions = typographyTokenDefinitions;
//# sourceMappingURL=typography.js.map
